import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { format, isAfter, isValid, parse, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import { CalendarIcon, CalendarRangeIcon } from 'lucide-react'
import { resolveDatePreset } from '../lib/date-presets'
import {
  DEFAULT_DATE_RANGE_GUARD,
  getMonthCount,
  isLargeDateRange,
} from '../lib/date-range-guard'
import type { DatePreset } from '../lib/date-presets'
import type { DateRangeGuardConfig } from '../lib/date-range-guard'
import type { DateRange as RdpDateRange } from 'react-day-picker'
import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

const presets: Array<DatePreset> = [
  'today',
  'yesterday',
  'this-week',
  'this-month',
  'this-quarter',
  'this-year',
  'last-month',
]

function parseDate(str: string): Date | undefined {
  if (!str) return undefined
  let d = parse(str, 'yyyy-MM-dd', new Date())
  if (isValid(d)) return d
  d = parse(str, 'dd.MM.yyyy', new Date())
  if (isValid(d)) return d
  return undefined
}

function toIso(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

function toDisplay(str: string): string {
  const d = parseDate(str)
  return d ? format(d, 'dd.MM.yyyy') : str
}

function autocompleteDate(input: string): string {
  const trimmed = input.replace(/\.$/, '')
  const parts = trimmed.split('.')
  if (parts.length === 2 && parts[0].length <= 2 && parts[1].length <= 2) {
    return `${parts[0].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${new Date().getFullYear()}`
  }
  if (parts.length === 3 && parts[2].length === 2) {
    return `${parts[0]}.${parts[1]}.20${parts[2]}`
  }
  return input
}

interface DateRangeFilterProps {
  dateFrom: string
  dateTo: string
  onApply: (dateFrom: string, dateTo: string) => void
  dateRangeGuard?: DateRangeGuardConfig
}

export function DateRangeFilter({
  dateFrom,
  dateTo,
  onApply,
  dateRangeGuard = DEFAULT_DATE_RANGE_GUARD,
}: DateRangeFilterProps) {
  const { t } = useTranslation('reports')
  const presetLabels: Record<DatePreset, string> = {
    today: t('dateRange.presetToday'),
    yesterday: t('dateRange.presetYesterday'),
    'this-week': t('dateRange.presetThisWeek'),
    'this-month': t('dateRange.presetThisMonth'),
    'this-quarter': t('dateRange.presetThisQuarter'),
    'this-year': t('dateRange.presetThisYear'),
    'last-month': t('dateRange.presetLastMonth'),
  }
  const [rangeOpen, setRangeOpen] = useState(false)

  const [pendingDateFrom, setPendingDateFrom] = useState<string | null>(null)
  const [pendingDateTo, setPendingDateTo] = useState<string | null>(null)

  const [showLargeRangeDialog, setShowLargeRangeDialog] = useState(false)
  const [pendingApplyFrom, setPendingApplyFrom] = useState<string | undefined>(
    undefined,
  )
  const [pendingApplyTo, setPendingApplyTo] = useState<string | undefined>(
    undefined,
  )

  const [dateRangeError, setDateRangeError] = useState<string | null>(null)

  const hasPendingChanges = pendingDateFrom !== null || pendingDateTo !== null

  const displayDateFrom = pendingDateFrom ?? dateFrom
  const displayDateTo = pendingDateTo ?? dateTo

  const rangeSelected: RdpDateRange | undefined =
    parseDate(displayDateFrom) || parseDate(displayDateTo)
      ? { from: parseDate(displayDateFrom), to: parseDate(displayDateTo) }
      : undefined

  const applyDateRange = useCallback(
    (from?: string, to?: string) => {
      const finalFrom = from ?? pendingDateFrom ?? dateFrom
      const finalTo = to ?? pendingDateTo ?? dateTo

      if (finalFrom && finalTo) {
        try {
          const fromDate = parseISO(finalFrom)
          const toDate = parseISO(finalTo)
          if (isAfter(fromDate, toDate)) {
            setDateRangeError(t('dateRange.fromAfterTo'))
            return
          }
        } catch {
          // ignore parse errors
        }
      }
      setDateRangeError(null)

      if (isLargeDateRange(finalFrom, finalTo, dateRangeGuard)) {
        setPendingApplyFrom(finalFrom)
        setPendingApplyTo(finalTo)
        setShowLargeRangeDialog(true)
        return
      }

      onApply(finalFrom, finalTo)
      setPendingDateFrom(null)
      setPendingDateTo(null)
    },
    [
      pendingDateFrom,
      pendingDateTo,
      dateFrom,
      dateTo,
      onApply,
      dateRangeGuard,
      t,
    ],
  )

  const forceApply = useCallback(
    (from?: string, to?: string) => {
      if (from !== undefined && to !== undefined) {
        onApply(from, to)
      }
      setPendingDateFrom(null)
      setPendingDateTo(null)
      setShowLargeRangeDialog(false)
    },
    [onApply],
  )

  function handleRangeSelect(range: RdpDateRange | undefined) {
    if (range?.from) {
      setPendingDateFrom(toIso(range.from))
    }
    if (range?.to) {
      setPendingDateTo(toIso(range.to))
    }
  }

  function handleRangePopoverChange(open: boolean) {
    setRangeOpen(open)
    if (!open && (pendingDateFrom !== null || pendingDateTo !== null)) {
      applyDateRange()
    }
  }

  function applyPreset(preset: DatePreset) {
    const range = resolveDatePreset(preset)
    setPendingDateFrom(null)
    setPendingDateTo(null)
    setDateRangeError(null)
    setRangeOpen(false)
    applyDateRange(range.from, range.to)
  }

  function handleContainerKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && hasPendingChanges) {
      e.preventDefault()
      applyDateRange()
    }
  }

  const monthCount = getMonthCount(pendingApplyFrom, pendingApplyTo)

  return (
    <>
      <div
        className="relative flex items-center gap-1"
        onKeyDown={handleContainerKeyDown}
      >
        <SingleDatePicker
          value={displayDateFrom}
          onChange={(iso) => {
            setPendingDateFrom(iso)
            setDateRangeError(null)
          }}
          onEnterApply={(iso) => {
            setPendingDateFrom(null)
            setPendingDateTo(null)
            setDateRangeError(null)
            applyDateRange(iso, undefined)
          }}
          placeholder={t('dateRange.from')}
          hasError={dateRangeError !== null}
        />
        <span className="text-xs text-muted-foreground">–</span>
        <SingleDatePicker
          value={displayDateTo}
          onChange={(iso) => {
            setPendingDateTo(iso)
            setDateRangeError(null)
          }}
          onEnterApply={(iso) => {
            setPendingDateFrom(null)
            setPendingDateTo(null)
            setDateRangeError(null)
            applyDateRange(undefined, iso)
          }}
          placeholder={t('dateRange.to')}
          hasError={dateRangeError !== null}
        />
        <Popover open={rangeOpen} onOpenChange={handleRangePopoverChange}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 gap-1 px-1.5 text-muted-foreground"
            >
              <CalendarRangeIcon className="size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex w-auto p-0" align="end">
            <div className="flex flex-col gap-1 border-r p-2">
              {presets.map((p) => (
                <Button
                  key={p}
                  variant="ghost"
                  size="sm"
                  className="h-8 justify-start text-sm"
                  onClick={() => applyPreset(p)}
                >
                  {presetLabels[p]}
                </Button>
              ))}
            </div>
            <Calendar
              mode="range"
              captionLayout="dropdown"
              showWeekNumber
              selected={rangeSelected}
              onSelect={handleRangeSelect}
              numberOfMonths={2}
              locale={de}
              weekStartsOn={1}
              defaultMonth={parseDate(displayDateFrom)}
              startMonth={new Date(2010, 0)}
              endMonth={new Date(new Date().getFullYear() + 2, 11)}
            />
          </PopoverContent>
        </Popover>

        {hasPendingChanges && !dateRangeError && (
          <Button
            variant="default"
            size="sm"
            className="h-9"
            onClick={() => applyDateRange()}
          >
            {t('dateRange.apply')}
          </Button>
        )}
        {dateRangeError && (
          <div className="text-xs text-destructive whitespace-nowrap">
            {dateRangeError}
          </div>
        )}
      </div>

      <AlertDialog
        open={showLargeRangeDialog}
        onOpenChange={setShowLargeRangeDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dateRange.largeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dateRange.largeDescription', { months: monthCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowLargeRangeDialog(false)}>
              {t('dateRange.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => forceApply(pendingApplyFrom, pendingApplyTo)}
            >
              {t('dateRange.continue')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

interface SingleDatePickerProps {
  value: string
  onChange: (iso: string) => void
  onEnterApply: (iso: string) => void
  placeholder?: string
  hasError?: boolean
}

function SingleDatePicker({
  value,
  onChange,
  onEnterApply,
  placeholder,
  hasError,
}: SingleDatePickerProps) {
  const [open, setOpen] = useState(false)
  const [localValue, setLocalValue] = useState(toDisplay(value))
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setLocalValue(toDisplay(value))
  }, [value])

  function handleInputChange(raw: string) {
    const digits = raw.replace(/[^\d]/g, '')

    let formatted = ''
    for (let i = 0; i < digits.length && i < 8; i++) {
      if (i === 2 || i === 4) formatted += '.'
      formatted += digits[i]
    }
    if (digits.length === 2 || digits.length === 4) formatted += '.'

    setLocalValue(formatted)
  }

  function handleInputBlur() {
    const completed = autocompleteDate(localValue)
    const d = parseDate(completed)
    if (d) {
      onChange(toIso(d))
    } else if (localValue === '') {
      onChange('')
    } else {
      setLocalValue(toDisplay(value))
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      const completed = autocompleteDate(localValue)
      const d = parseDate(completed)
      if (d) {
        onEnterApply(toIso(d))
      } else if (localValue === '') {
        onChange('')
      } else {
        setLocalValue(toDisplay(value))
      }
      inputRef.current?.blur()
    }
  }

  function handleCalendarSelect(date: Date | undefined) {
    if (date) {
      setOpen(false)
      onChange(toIso(date))
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex items-center">
        <Input
          ref={inputRef}
          value={localValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          onFocus={(e) => e.target.select()}
          placeholder={placeholder}
          className={`h-9 w-28 text-sm ${hasError ? 'border-destructive' : ''}`}
        />
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 p-0"
            tabIndex={-1}
          >
            <CalendarIcon className="size-3.5 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          showWeekNumber
          selected={parseDate(value)}
          onSelect={handleCalendarSelect}
          locale={de}
          weekStartsOn={1}
          defaultMonth={parseDate(value)}
          startMonth={new Date(2010, 0)}
          endMonth={new Date(new Date().getFullYear() + 2, 11)}
        />
      </PopoverContent>
    </Popover>
  )
}
